import Api from 'entities/helpers/api';
import colors from 'base/colors';
import FoldersCard from 'apps/folder/components/detail/folders-card';
import SegmentSource from 'apps/filters/sources/segment-source';
import ListIndexTable from 'apps/lists/components/index/list-index-table';
import ListIndexActions from 'apps/lists/components/index/list-index-actions';
import useFeaturedLinks from 'apps/filters/hooks/use-featured-links';
import CommonDeleteModal from 'apps/folder/components/modal/common-delete-modal';
import RecentlyViewedLists from 'apps/lists/components/index/recently-viewed-lists';
import SavedSearchesfeatureLink from 'apps/segments/saved-search-feature-link';
import { useLists } from 'apps/lists/hooks/use-lists';
import { useOpenClose } from '@evertrue/sdk';
import { useState, useRef } from 'react';
import { SAVED_SEARCH_SEGMENT_FILTERS } from 'apps/filters/featured-segments-default';
import { Card, Icon, Button, PageHeader } from '@evertrue/et-components';

const SavedSearchIndexController = () => {
  // Fetch Lists
  const ownedOnly = false; // in the future, it would be nice to add a toggle to the toolbar for this, as a quick filter
  const {
    total,
    lists,
    loading,
    deleteList,
    deleteLists,
    search,
    handleSearchChange,
    sortModel,
    handleSortChange,
    paginationModel,
    setPaginationModel,
    rowSelectionModel,
    updateRowSelectionModel,
    clearSelections,
    enableAllActions,
  } = useLists('dynamic', ownedOnly);

  const handleBulkDelete = () => {
    deleteLists(Array.from(rowSelectionModel.ids));
    clearSelections();
    closeBulkDeleteModal();
  };

  const folderCardRef = useRef();
  const featuredLinks = useFeaturedLinks(SAVED_SEARCH_SEGMENT_FILTERS);
  const [isExpanded, setIsExpanded] = useState(true);
  const [bulkDeleteModalOpen, openBulkDeleteModal, closeBulkDeleteModal] = useOpenClose();

  const updateSavedSearchPref = (list, listId, type) => {
    const frequency = list[`${type}_frequency`];
    let data = {};
    if (frequency === 'never') {
      data[`${type}_enabled`] = false;
    } else {
      data[`${type}_enabled`] = true;
      data[`${type}_frequency`] = frequency;
    }
    return Api.EMS.PREFS_BY_KEY.put({
      urlArgs: { key: `list:${listId}:` },
      data: JSON.stringify(data),
      success_message: 'Notification preferences updated',
      error_message: 'Failed to update notification preferences',
    });
  };

  const updateRow = async (newRow, oldRow) => {
    const updates = [];
    if (newRow.email_frequency !== oldRow.email_frequency) {
      updates.push(updateSavedSearchPref(newRow, newRow.id, 'email'));
    }
    if (newRow.push_frequency !== oldRow.push_frequency) {
      updates.push(updateSavedSearchPref(newRow, newRow.id, 'push'));
    }
    await Promise.all(updates);
    return newRow;
  };

  return (
    <>
      <PageHeader
        title="Saved Searches"
        subtitle="Set up notifications below to be alerted whenever a new prospect meets the criteria of your search. You can
          adjust the frequency of these notifications at any time."
        actions={
          <ListIndexActions
            listType="dynamic"
            enableAllActions={enableAllActions}
            openBulkDeleteModal={openBulkDeleteModal}
            folderCardRef={folderCardRef}
            anyListSelected={rowSelectionModel.ids.size > 0}
          />
        }
      />
      {/* Saved Searches has a unique Empty State that does not look good on a table, so we conditionally render it */}
      {lists.length === 0 && !loading && !search ? (
        <Card className="segments-page--empty">
          <Icon icon="segment" size={3} style={{ color: colors.blue500 }} />
          <h2 className="segments-page--empty-title">Never miss a prospect with Saved Searches!</h2>
          <div className="segments-page--empty-subtitle">
            Here are some favorites from around the advancement organization to get you started. Save a search and sign
            up to receive email and push notifications whenever a new constituent meets your criteria.
          </div>
          <div className="segments-page--empty-featured">{featuredLinks}</div>
        </Card>
      ) : (
        <div className="lists-page-controller">
          {/* Main Table */}
          <div className="saved-searches-table" style={{ height: 'calc(100vh - 133px)', flex: 1 }}>
            <ListIndexTable
              listType="dynamic"
              tableKey="saved-search-index-table"
              lists={lists}
              total={total}
              loading={loading}
              deleteList={deleteList}
              search={search}
              onSearchChange={handleSearchChange}
              sortModel={sortModel}
              setSortModel={handleSortChange}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
              rowSelectionModel={rowSelectionModel}
              updateRowSelectionModel={updateRowSelectionModel}
              emptyStateIcon="segment"
              emptyStateMessage="No Saved Searches found"
              onRowUpdate={updateRow}
            />
          </div>
          {/* Side Bar */}
          <div style={{ flex: 0.5 }}>
            <FoldersCard
              ref={folderCardRef}
              folderType="DYNAMIC"
              listIds={Array.from(rowSelectionModel.ids)}
              clearSelections={clearSelections}
            />
            <RecentlyViewedLists lists={lists} onClick={(id) => SegmentSource.applySegment(id)} />
            <Card
              header={
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="clickable-header"
                >
                  <h1 className="title-subtitle-500-20">Suggested Searches</h1>
                  <Button type="simple">
                    <Icon icon={isExpanded ? 'chevron-up' : 'chevron-down'} />
                  </Button>
                </div>
              }
              padding="16px"
            >
              {isExpanded && <SavedSearchesfeatureLink featuredLinks={featuredLinks} />}
            </Card>
          </div>
        </div>
      )}
      <CommonDeleteModal
        isOpen={bulkDeleteModalOpen}
        onClose={closeBulkDeleteModal}
        handleDelete={handleBulkDelete}
        title="Confirm Delete"
        body="Are you sure you want to delete these lists?"
        buttonLabel="Delete Lists"
      />
    </>
  );
};

export default SavedSearchIndexController;
